/* eslint-disable camelcase */
import React from "react"
import { Box } from "@chakra-ui/react"
import { ProductCardBadgeLabel } from "components/ProductCard/ProductCardBadgeLabel"
import { ProductCardLabel } from "components/ProductCard/ProductCardLabel"
import type { ImageProps } from "next/image"
import Image from "next/image"
import type { VariantType, VisualBynderType } from "types"
import { getStaticFile } from "utils/getStaticFile"
import { isStorybook } from "utils/storybookUtils"

type ProductImageProps = {
  variant: VariantType
  image: VisualBynderType
  position: number
  showLabel?: boolean
} & Pick<ImageProps, "objectFit" | "priority">

export const ProductCardImage: React.FC<ProductImageProps> = ({
  variant,
  image,
  position,
  showLabel = true,
  ...imageProps
}) => {
  if (!image) return null

  const { availability, badges } = variant
  const { description, thumbnails } = image

  const srcUrl = thumbnails?.size_OTF_BETA
  const outOfStock = availability?.status === "outofstock"

  const ProductBadgesOrLabels = () =>
    badges?.length ? (
      badges.map(
        (item) =>
          item && (
            <ProductCardBadgeLabel key={`badge-${item?.badge}`} badge={item} />
          )
      )
    ) : (
      <ProductCardLabel availability={availability} />
    )

  // Fix for NextImage SSR warning message: Image with src X is smaller than 40x40...
  // ...Consider removing the "placeholder='blur'" property to improve performance.
  const imgSize = 40

  return (
    <Box position="relative">
      <Box opacity={outOfStock ? "0.6" : ""}>
        {srcUrl &&
          (position >= 4 && !isStorybook ? (
            <Image
              {...imageProps}
              src={srcUrl}
              alt={description as string}
              placeholder="blur"
              blurDataURL={getStaticFile("/images/placeholder.svg")}
              width={imgSize}
              height={imgSize}
              quality={85}
              data-testid="image"
              unoptimized={
                process.env.STORYBOOK === "true" ||
                process.env.NODE_ENV === "test" ||
                /\.gif$/.test(srcUrl)
              }
              sizes="(max-width: 374px) 100vw, (max-width: 768px) 50vw, (max-width: 1440px) 33vw, 20vw"
              style={{
                width: "100%",
                height: "auto",
              }}
            />
          ) : (
            <>
              <Image
                {...imageProps}
                src={srcUrl}
                alt={description as string}
                width={imgSize}
                height={imgSize}
                quality={85}
                data-testid="image"
                unoptimized={
                  process.env.STORYBOOK === "true" ||
                  process.env.NODE_ENV === "test" ||
                  /\.gif$/.test(srcUrl)
                }
                priority={imageProps.priority ?? true}
                sizes="(max-width: 374px) 100vw, (max-width: 768px) 50vw, (max-width: 1440px) 33vw, 20vw"
                style={{
                  width: "100%",
                  height: "auto",
                }}
              />
            </>
          ))}
      </Box>
      {showLabel && ProductBadgesOrLabels()}
    </Box>
  )
}
